import React, { useRef, useState, useEffect } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TemplateWorksheets, { TemplateWorksheetRef } from './TemplateWorksheets';
import { Template } from '../../types/types';

interface TemplateEditorProps {
	templateId: number;
	open: boolean;
	onClose: () => void;
}

const TemplateEditorDialog: React.FC<TemplateEditorProps> = ({ templateId, open, onClose }) => {
	const [isDirty, setIsDirty] = useState(false);
	const [template, setTemplate] = useState<Template | null>(null);
	const worksheetsRef = useRef<TemplateWorksheetRef>(null);

	useEffect(() => {
		const fetchTemplate = async () => {
			try {
				const response = await fetch(`api/template/${templateId}`);

				if (!response.ok) {
					throw new Error('Failed to fetch template');
				}

				const data = await response.json();
				setTemplate(data);
			} catch (error) {
				console.error('Error fetching template:', error);
				alert('Failed to load template');
			}
		};

		if (templateId) {
			fetchTemplate();
		}
	}, [templateId]);

	const handleSave = async () => {
		try {
			if (worksheetsRef.current) {
				await worksheetsRef.current.save();
				onClose();
			}
		} catch (error) {
			console.error('Error saving template:', error);
			alert('Failed to save template');
		}
	};

	const handleCancel = () => {
		if (worksheetsRef.current) {
			worksheetsRef.current.reset();
		}

		onClose();
	};

	const handleClose = (_: unknown, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason === 'backdropClick') {
			return;	// ignore backdrop click
		}
		
		if (isDirty) {
			if (window.confirm('Discard changes?')) {
				onClose();
			}
		}
		else {
			onClose();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="lg"
			fullWidth
			disableEscapeKeyDown
		>
			{template && (
				<>
					<DialogTitle sx={{ m: 0, p: 2 }}>
						{template.name}
						<IconButton
							aria-label="close"
							onClick={() => handleClose(null, 'escapeKeyDown')}
							sx={{
								position: 'absolute',
								right: 8,
								top: 8,
							}}
						>
							<CloseIcon />
						</IconButton>
					</DialogTitle>

					<DialogContent>
						<TemplateWorksheets
							incomingTemplate={template}
							onDirtyChange={setIsDirty}
							ref={worksheetsRef}
						/>
					</DialogContent>
				</>
			)}

			<DialogActions>
				<Button
					variant="outlined"
					onClick={handleCancel}
					disabled={!isDirty}
				>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSave}
					disabled={!isDirty}
				>
					Save Changes
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TemplateEditorDialog;
